import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "contexts/i18nContext";

// Helper function to truncate file names
const truncateFileName = (fileName: string, maxLength: number = 30): string => {
  if (fileName.length <= maxLength) return fileName;
  return fileName.substring(0, maxLength - 3) + "...";
};

export interface DocumentData {
  uu_id: string;
  document_type: string;
  document_uuid: string;
  document_path: string;
  created_at: string;
  updated_at: string;
  reporter?: string;
}

export interface DocumentViewerProps {
  label?: string;
  documents: DocumentData[];
  baseUrl?: string;
  className?: string;
  showMetadata?: boolean;
  onDownload?: (documentData: DocumentData) => void;
  /** Controlled open state (optional). If provided, component behaves as controlled modal. */
  isOpen?: boolean;
  /** Callback when modal is requested to close (optional). */
  onClose?: () => void;
  layout?: "grid" | "list" | "gallery";
  columns?: number;
  hide?: boolean;
  hideEmptyFile?: boolean;
}

const DocumentViewerModal: React.FC<DocumentViewerProps> = ({
  label = "",
  documents = [],
  baseUrl = "",
  className = "",
  showMetadata = true,
  onDownload,
  isOpen,
  onClose,
  layout = "list",
  columns = 3,
  hide = false,
  hideEmptyFile = false,
}) => {
  const { t } = useTranslation();
  const [fileUrls, setFileUrls] = useState<{ [key: string]: string }>({});
  const [fileTypes, setFileTypes] = useState<{ [key: string]: string }>({});
  const [loadingStates, setLoadingStates] = useState<{
    [key: string]: boolean;
  }>({});
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [currentDocument, setCurrentDocument] = useState<DocumentData | null>(
    null,
  );
  const [pdfBlobUrls, setPdfBlobUrls] = useState<{ [key: string]: string }>({});

  // Safe document data with fallbacks
  const safeDocuments = React.useMemo(() => {
    return documents.map((doc) => ({
      uu_id: doc?.uu_id || "N/A",
      document_type: doc?.document_type || "unknown_document",
      document_uuid: doc?.document_uuid || "N/A",
      document_path: doc?.document_path || "",
      created_at: doc?.created_at || new Date().toISOString(),
      updated_at: doc?.updated_at || new Date().toISOString(),
      reporter: doc?.reporter || "N/A",
    }));
  }, [documents]);

  // Enhanced URL construction with debugging
  const constructFileUrl = useCallback(
    (doc: DocumentData): string => {
      // If baseUrl is provided, use it with document_uuid
      if (baseUrl) {
        // Remove trailing slash if exists
        const cleanBaseUrl = baseUrl.replace(/\/$/, "");
        const constructedUrl = `${cleanBaseUrl}/${doc.document_uuid}`;
        return constructedUrl;
      }

      // If no baseUrl but document_path looks like a full URL, use it directly
      if (doc.document_path.startsWith("http")) {
        return doc.document_path;
      }

      return "";
    },
    [baseUrl],
  );

  // Initialize all documents
  useEffect(() => {
    const initializeDocuments = async () => {
      const newFileUrls: { [key: string]: string } = {};
      const newFileTypes: { [key: string]: string } = {};
      const newLoadingStates: { [key: string]: boolean } = {};
      const newErrors: { [key: string]: string } = {};

      safeDocuments.forEach((doc) => {
        try {
          newLoadingStates[doc.document_uuid] = true;

          // Use the new URL construction method
          const fileUrl = constructFileUrl(doc);

          if (!fileUrl) {
            newErrors[doc.document_uuid] = t("cannotConstructFileUrl");
            return;
          }

          // Extract file extension from document_path
          const fileExtension =
            doc.document_path.split(".").pop()?.toLowerCase() || "";

          newFileUrls[doc.document_uuid] = fileUrl;
          newFileTypes[doc.document_uuid] = getFileType(fileExtension);
        } catch (err) {
          newErrors[doc.document_uuid] = t("failedToInitializeDocument");
        } finally {
          newLoadingStates[doc.document_uuid] = false;
        }
      });

      setFileUrls(newFileUrls);
      setFileTypes(newFileTypes);
      setLoadingStates(newLoadingStates);
      setErrors(newErrors);
    };

    if (safeDocuments.length > 0) {
      initializeDocuments();
    }
  }, [safeDocuments, constructFileUrl, t]);

  const getFileType = (extension: string): string => {
    const imageTypes = ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"];
    const videoTypes = ["mp4", "avi", "mov", "wmv", "flv", "webm", "mkv"];
    const audioTypes = ["mp3", "wav", "ogg", "aac", "flac"];
    const pdfTypes = ["pdf"];
    const documentTypes = ["doc", "docx", "txt", "rtf"];
    const spreadsheetTypes = ["xls", "xlsx", "csv"];

    if (imageTypes.includes(extension)) return "image";
    if (videoTypes.includes(extension)) return "video";
    if (audioTypes.includes(extension)) return "audio";
    if (pdfTypes.includes(extension)) return "pdf";
    if (documentTypes.includes(extension)) return "document";
    if (spreadsheetTypes.includes(extension)) return "spreadsheet";

    return "other";
  };

  // Generic function to fetch any file as blob with authorization header
  const fetchFileAsBlob = async (fileUrl: string, documentUuid: string): Promise<string | null> => {
    try {
      setLoadingStates((prev) => ({ ...prev, [documentUuid]: true }));
      setErrors((prev) => ({ ...prev, [documentUuid]: "" }));

      console.log(`Fetching file from: ${fileUrl}`);

      const token = sessionStorage.getItem("authToken");
      const response = await fetch(fileUrl, {
        credentials: "include",
        headers: token
          ? { Authorization: `Bearer ${token}` }
          : undefined,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();

      if (blob.size === 0) {
        throw new Error(t("emptyFileReceived"));
      }

      const blobUrl = URL.createObjectURL(blob);

      setPdfBlobUrls((prev) => ({ ...prev, [documentUuid]: blobUrl }));
      setLoadingStates((prev) => ({ ...prev, [documentUuid]: false }));

      return blobUrl;
    } catch (error) {
      console.error("Error fetching file:", error);
      const errorMessage =
        error instanceof Error ? error.message : t("unknownError");
      setErrors((prev) => ({
        ...prev,
        [documentUuid]: `${t("failedToLoadPdf")}: ${errorMessage}`,
      }));
      setLoadingStates((prev) => ({ ...prev, [documentUuid]: false }));
      return null;
    }
  };

  // Legacy alias for backward compatibility
  const fetchPdfAsBlob = fetchFileAsBlob;

  const handleDownload = async (doc: DocumentData) => {
    try {
      if (onDownload) {
        onDownload(doc);
      }

      const fileUrl = fileUrls[doc.document_uuid];
      if (!fileUrl) {
        setErrors((prev) => ({
          ...prev,
          [doc.document_uuid]: t("noFileUrlAvailableForDownload"),
        }));
        return;
      }

      // Determine file name for download
      const fileName =
        doc.document_path.split("/").pop() ||
        `document-${doc.document_uuid}`;

      // If blob URL already exists (from PDF viewer or previous fetch), use it
      if (pdfBlobUrls[doc.document_uuid]) {
        const link = document.createElement("a");
        link.href = pdfBlobUrls[doc.document_uuid];
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }

      // Fetch the file as a blob to force download instead of opening in browser
      const token = sessionStorage.getItem("authToken");
      const response = await fetch(fileUrl, {
        credentials: "include",
        headers: token
          ? { Authorization: `Bearer ${token}` }
          : undefined,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      // Cache the blob URL for future use
      setPdfBlobUrls((prev) => ({ ...prev, [doc.document_uuid]: blobUrl }));

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL after a short delay
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
        setPdfBlobUrls((prev) => {
          const next = { ...prev };
          delete next[doc.document_uuid];
          return next;
        });
      }, 60000);
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        [doc.document_uuid]: t("failedToDownloadFile"),
      }));
    }
  };

  const getFileIcon = (fileType: string) => {
    switch (fileType) {
      case "image":
        return "🖼️";
      case "pdf":
        return "📄";
      case "video":
        return "🎥";
      case "audio":
        return "🔊";
      case "document":
        return "📝";
      case "spreadsheet":
        return "📊";
      default:
        return "📎";
    }
  };

  const formatDocumentType = (docType: string | undefined): string => {
    if (!docType) return t("unknownDocument");
    if (docType === "meeting") return t("minute");
    return docType.replace(/_/g, " ");
  };

  const openModal = async (doc: DocumentData) => {
    const fileType = fileTypes[doc.document_uuid];
    const fileUrl = fileUrls[doc.document_uuid];

    // Always set current document and open modal immediately
    setCurrentDocument(doc);
    setIsModalOpen(true);

    // For PDFs, fetch as blob in background
    if (fileType === "pdf" && fileUrl && !pdfBlobUrls[doc.document_uuid]) {
      await fetchPdfAsBlob(fileUrl, doc.document_uuid);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentDocument(null);
    if (onClose) onClose();
  };

  // Sync controlled `isOpen` prop with internal modal state
  useEffect(() => {
    if (typeof isOpen !== "undefined") {
      setIsModalOpen(!!isOpen);
      if (isOpen) {
        // If opening via controlled prop, select first document by default
        if (!currentDocument && safeDocuments && safeDocuments.length > 0) {
          setCurrentDocument(safeDocuments[0]);
        }
      } else {
        // closing
        setCurrentDocument(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, safeDocuments]);

  // Clean up blob URLs when component unmounts
  useEffect(() => {
    return () => {
      Object.values(pdfBlobUrls).forEach((blobUrl) => {
        URL.revokeObjectURL(blobUrl);
      });
    };
  }, [pdfBlobUrls]);

  // Enhanced PDF Viewer that uses blob URLs (no CORS issues)
  const BlobPDFViewer = ({ documentData }: { documentData: DocumentData }) => {
    const fileUrl = pdfBlobUrls[documentData.document_uuid];
    const isLoading = loadingStates[documentData.document_uuid];
    const error = errors[documentData.document_uuid];

    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center h-96 bg-gray-50 rounded-lg">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <span className="text-gray-600">{t("loadingPdf")}</span>
        </div>
      );
    }

    if (error || !fileUrl) {
      return (
        <div className="flex flex-col items-center justify-center h-96 bg-red-50 border border-red-200 rounded-lg p-8">
          <div className="text-4xl mb-4">❌</div>
          <h3 className="text-xl font-semibold text-red-800 mb-2">
            {t("failedToLoadPdf")}
          </h3>
          <p className="text-red-600 text-center mb-6">
            {error || t("pdfCouldNotBeLoaded")}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() =>
                window.open(fileUrls[documentData.document_uuid], "_blank")
              }
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              {t("openInNewTab")}
            </button>
            <button
              onClick={() => handleDownload(documentData)}
              className="px-4 py-2 bg-red-800 text-white rounded hover:bg-red-900 transition-colors"
            >
              {t("downloadPdf")}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="w-full h-full bg-white rounded-lg overflow-hidden flex flex-col">
        <div className="flex-1 min-h-0">
          <iframe
            src={`${fileUrl}#toolbar=0&navpanes=0&scrollbar=0`}
            width="100%"
            height="100%"
            className="min-h-[500px] w-full"
            title={`PDF Document - ${documentData.document_type}`}
          />
        </div>
        <div className="p-3 bg-gray-50 border-t border-gray-200">
          <p className="text-sm text-gray-600 text-center">
            {t("pdfLoadedSuccessfully")}
          </p>
        </div>
      </div>
    );
  };

  const renderDocumentThumbnail = (doc: DocumentData) => {
    const fileType = fileTypes[doc.document_uuid];
    const fileUrl = fileUrls[doc.document_uuid];
    const isLoading = loadingStates[doc.document_uuid];
    const error = errors[doc.document_uuid];

    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center h-32 text-gray-500 bg-gray-100 rounded-lg">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mb-2"></div>
          <span className="text-xs">{t("loading")}</span>
        </div>
      );
    }

    if (error || !fileUrl) {
      return (
        <div className="flex flex-col items-center justify-center h-32 text-red-600 bg-red-50 rounded-lg p-3">
          <div className="text-lg mb-1">❌</div>
          <span className="text-xs text-center">{error || t("noFileUrl")}</span>
        </div>
      );
    }

    if (fileType === "image") {
      return (
        <img
          src={fileUrl}
          alt={`Thumbnail for ${doc.document_type}`}
          className="w-full h-32 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
          onClick={() => openModal(doc)}
          onError={() =>
            setErrors((prev) => ({
              ...prev,
              [doc.document_uuid]: t("failedToLoadImage"),
            }))
          }
        />
      );
    }

    return (
      <div
        className="flex flex-col items-center justify-center h-32 bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors p-3"
        onClick={() => openModal(doc)}
      >
        <div className="text-2xl mb-2">{getFileIcon(fileType)}</div>
        <span className="text-xs text-gray-600 text-center capitalize">
          {fileType} {t("file")}
        </span>
        {error && <span className="text-xs text-red-500 mt-1">{error}</span>}
      </div>
    );
  };

  const renderGridView = () => {
    const gridCols =
      {
        1: "grid-cols-1",
        2: "grid-cols-2",
        3: "grid-cols-3",
        4: "grid-cols-4",
        5: "grid-cols-5",
        6: "grid-cols-6",
      }[columns] || "grid-cols-3";

    return (
      <div className={`grid ${gridCols} gap-4`}>
        {safeDocuments.map((doc) => (
          <div
            key={doc.document_uuid}
            className="border border-gray-200 rounded-lg p-3 bg-white shadow-sm hover:shadow-md transition-shadow"
          >
            {renderDocumentThumbnail(doc)}
            <div className="mt-2">
              <h3 className="text-sm font-medium text-gray-900 truncate capitalize">
                {formatDocumentType(doc.document_type)}
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                {new Date(doc.created_at).toLocaleDateString()}
              </p>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload(doc);
                }}
                disabled={
                  !fileUrls[doc.document_uuid] || !!errors[doc.document_uuid]
                }
                className={`w-full mt-2 px-2 py-1 text-xs rounded transition-colors ${
                  !fileUrls[doc.document_uuid] || errors[doc.document_uuid]
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-red-800 text-white hover:bg-red-900"
                }`}
              >
                {!fileUrls[doc.document_uuid] || errors[doc.document_uuid]
                  ? t("unavailable")
                  : t("download")}
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderListView = () => {
    return (
      <div className="space-y-3">
        {safeDocuments.map((doc) => {
          const fileType = fileTypes[doc.document_uuid];
          const fileUrl = fileUrls[doc.document_uuid];
          const isLoading = loadingStates[doc.document_uuid];
          const error = errors[doc.document_uuid];

          return (
            <div
              key={doc.document_uuid}
              className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex-shrink-0">
                {isLoading ? (
                  <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  </div>
                ) : error ? (
                  <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded text-red-600">
                    ❌
                  </div>
                ) : (
                  <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded text-2xl">
                    {getFileIcon(fileType)}
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium text-gray-900 truncate capitalize">
                  {formatDocumentType(doc.document_type)}
                </h3>
                <p className="text-xs text-gray-500 truncate">
                  {truncateFileName(doc.document_path.split("/").pop() || "")}
                </p>
                <p className="text-xs text-gray-400">
                  {t("created")}:{" "}
                  {new Date(doc.created_at).toLocaleDateString()}
                </p>
                {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => openModal(doc)}
                  disabled={!fileUrl || !!error}
                  className={`px-3 py-1 text-xs rounded transition-colors ${
                    !fileUrl || error
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-gray-500 text-white hover:bg-gray-700"
                  }`}
                >
                  {t("view")}
                </button>
                <button
                  onClick={() => handleDownload(doc)}
                  disabled={!fileUrl || !!error}
                  className={`px-3 py-1 text-xs rounded transition-colors ${
                    !fileUrl || error
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-red-800 text-white hover:bg-red-900"
                  }`}
                >
                  {t("download")}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderModalContent = () => {
    if (!currentDocument) return null;

    const fileType = fileTypes[currentDocument.document_uuid];
    const fileUrl = fileUrls[currentDocument.document_uuid];
    const isLoading = loadingStates[currentDocument.document_uuid];
    const error = errors[currentDocument.document_uuid];

    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          {t("loadingDocument")}
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center h-64 text-red-700 bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="text-2xl mb-2">❌</div>
          <div className="text-center">
            <div className="font-medium mb-2">{t("errorLoadingDocument")}</div>
            <div className="text-sm mb-4">{error}</div>
            <button
              onClick={() => handleDownload(currentDocument)}
              className="px-4 py-2 bg-red-800 text-white rounded hover:bg-red-900 transition-colors"
            >
              {t("tryDownloadAnyway")}
            </button>
          </div>
        </div>
      );
    }

    switch (fileType) {
      case "image": {
        const imageBlobUrl = pdfBlobUrls[currentDocument.document_uuid];
        if (!imageBlobUrl && fileUrl && !loadingStates[currentDocument.document_uuid]) {
          fetchFileAsBlob(fileUrl, currentDocument.document_uuid);
        }
        return (
          <div className="flex justify-center bg-black bg-opacity-5 rounded-lg p-4">
            {imageBlobUrl ? (
              <img
                src={imageBlobUrl}
                alt={`Document ${currentDocument.document_uuid}`}
                className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-sm"
                onError={() =>
                  setErrors((prev) => ({
                    ...prev,
                    [currentDocument.document_uuid]: t("failedToLoadImage"),
                  }))
                }
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                <span className="text-gray-600">{t("loadingDocument")}</span>
              </div>
            )}
          </div>
        );
      }

      case "pdf":
        return <BlobPDFViewer documentData={currentDocument} />;

      case "video": {
        const videoBlobUrl = pdfBlobUrls[currentDocument.document_uuid];
        if (!videoBlobUrl && fileUrl && !loadingStates[currentDocument.document_uuid]) {
          fetchFileAsBlob(fileUrl, currentDocument.document_uuid);
        }
        return (
          <div className="flex justify-center bg-black rounded-lg p-4">
            {videoBlobUrl ? (
              <video
                controls
                src={videoBlobUrl}
                className="max-w-full max-h-[70vh]"
                onError={() =>
                  setErrors((prev) => ({
                    ...prev,
                    [currentDocument.document_uuid]: t("failedToLoadVideo"),
                  }))
                }
              >
                {t("browserDoesNotSupportVideo")}
              </video>
            ) : (
              <div className="flex flex-col items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                <span className="text-gray-600">{t("loadingDocument")}</span>
              </div>
            )}
          </div>
        );
      }

      case "audio": {
        const audioBlobUrl = pdfBlobUrls[currentDocument.document_uuid];
        if (!audioBlobUrl && fileUrl && !loadingStates[currentDocument.document_uuid]) {
          fetchFileAsBlob(fileUrl, currentDocument.document_uuid);
        }
        return (
          <div className="flex justify-center bg-gray-100 rounded-lg p-8">
            {audioBlobUrl ? (
              <audio
                controls
                src={audioBlobUrl}
                className="w-full max-w-md"
                onError={() =>
                  setErrors((prev) => ({
                    ...prev,
                    [currentDocument.document_uuid]: t("failedToLoadAudio"),
                  }))
                }
              >
                {t("browserDoesNotSupportAudio")}
              </audio>
            ) : (
              <div className="flex flex-col items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
                <span className="text-gray-600">{t("loadingDocument")}</span>
              </div>
            )}
          </div>
        );
      }

      default:
        return (
          <div className="flex flex-col items-center justify-center h-64 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <div className="text-4xl mb-4">{getFileIcon(fileType)}</div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">
              {fileType.charAt(0).toUpperCase() + fileType.slice(1)} {t("file")}
            </h4>
            <p className="text-gray-600 mb-4 max-w-md">
              {t("fileCannotBePreviewed")}
            </p>
            <button
              onClick={() => handleDownload(currentDocument)}
              className="flex items-center gap-2 px-4 py-2 bg-red-800 text-white rounded-md hover:bg-red-900 transition-colors"
            >
              <span>📥</span>
              {t("downloadFile")}
            </button>
          </div>
        );
    }
  };

  // Don't render anything if no documents
  if ((!documents || documents.length === 0) && !hideEmptyFile) {
    return null;
  }

  return (
    <div className={className}>
      {/* Document Gallery/List */}
      {label && !hideEmptyFile && (
        <h2 className="text-sm font-semibold text-gray-700 mb-2">{label}</h2>
      )}

      {!hide && (
        <div className="mb-4">
          {layout === "grid" && renderGridView()}
          {layout === "list" && renderListView()}
        </div>
      )}

      {/* Modal */}
      {isModalOpen && currentDocument && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-75"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="text-2xl">
                  {getFileIcon(fileTypes[currentDocument.document_uuid])}
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 capitalize">
                    {formatDocumentType(currentDocument.document_type)}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {truncateFileName(
                      currentDocument.document_path.split("/").pop() || "",
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleDownload(currentDocument)}
                  disabled={
                    loadingStates[currentDocument.document_uuid] ||
                    !!errors[currentDocument.document_uuid] ||
                    !fileUrls[currentDocument.document_uuid]
                  }
                  className={`
                    flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition-colors text-sm focus:outline-none focus:ring-2 focus:ring-offset-2
                    ${
                      loadingStates[currentDocument.document_uuid] ||
                      errors[currentDocument.document_uuid] ||
                      !fileUrls[currentDocument.document_uuid]
                        ? "bg-gray-300 cursor-not-allowed text-gray-500"
                        : "bg-red-800 hover:bg-red-900 text-white shadow-sm focus:ring-red-500"
                    }
                  `}
                >
                  <span>📥</span>
                  {t("download")}
                </button>
                <button
                  onClick={closeModal}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  aria-label={t("closeModal")}
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-auto p-6">
              {renderModalContent()}
            </div>

            {/* Modal Footer */}
            {showMetadata && (
              <div className="p-4 border-t border-gray-200 bg-gray-50">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">
                      {t("created")}:
                    </span>
                    <p className="text-gray-600">
                      {new Date(currentDocument.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">
                      {t("updated")}:
                    </span>
                    <p className="text-gray-600">
                      {new Date(currentDocument.updated_at).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">
                      {t("type")}:
                    </span>
                    <p className="text-gray-600 capitalize">
                      {formatDocumentType(currentDocument.document_type)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentViewerModal;
